import { EmptyState, ErrorState, LoadingState, RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import { fetchAdminEventQuestions, updateAdminEventQuestion } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type {
  EventQuestionAdmin,
  EventQuestionAdminUpdate,
  EventState,
} from "~/types/events-admin";

/** How often the list asks again while the broadcast runs — questions arrive live. */
const LIVE_POLL_MS = 10_000;
/** And otherwise: often enough to notice, rare enough to cost nothing. */
const IDLE_POLL_MS = 60_000;

/**
 * The moderator's list: every question about this webinar, newest first, ticked off as
 * they are answered on air.
 *
 * Polls faster while the event is live, because that is when the list is on a second
 * screen next to the presenter. Hiding is moderation, not deletion — a hidden question
 * leaves this list and stays in its asker's.
 */
export function EventQuestions({
  eventId,
  state,
}: {
  eventId: string;
  state: EventState;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const queryClient = useQueryClient();
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.eventQuestions(eventId, {
      unansweredOnly,
      includeHidden,
    }),
    queryFn: ({ signal }) =>
      fetchAdminEventQuestions(eventId, {
        unansweredOnly,
        includeHidden,
        signal,
      }),
    refetchInterval:
      state === "live" || state === "soon" ? LIVE_POLL_MS : IDLE_POLL_MS,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: EventQuestionAdminUpdate;
    }) => updateAdminEventQuestion(eventId, id, patch),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.eventQuestionsRoot(eventId),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) }),
      ]);
    },
  });

  const items = query.data?.items ?? [];

  return (
    <Paper
      component="section"
      variant="outlined"
      data-testid="event.questions"
      sx={{
        padding: 3,
        borderRadius: `${RADIUS.lg}px`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="H3" sx={{ color: "text.primary" }}>
          {t("eventEditor.questions.title")}
        </Typography>
        <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
          {t("eventEditor.questions.hint")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={unansweredOnly ? "unanswered" : "all"}
          onChange={(_event, value: string) =>
            setUnansweredOnly(value === "unanswered")
          }
        >
          <Tab
            label={t("eventEditor.questions.filter.all")}
            value="all"
            data-testid="event.questions-all"
          />
          <Tab
            label={t("eventEditor.questions.filter.unanswered")}
            value="unanswered"
            data-testid="event.questions-unanswered"
          />
        </Tabs>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeHidden}
              onChange={(changeEvent) =>
                setIncludeHidden(changeEvent.target.checked)
              }
              data-testid="event.questions-show-hidden"
            />
          }
          label={t("eventEditor.questions.showHidden")}
        />
      </Box>

      {query.data ? (
        <Typography
          variant="T3"
          sx={{ color: "text.muted" }}
          data-testid="event.questions-count"
        >
          {t("eventEditor.questions.count", {
            total: query.data.total,
            unanswered: query.data.unanswered,
          })}
        </Typography>
      ) : null}

      {query.isPending ? <LoadingState /> : null}
      {query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && items.length === 0 ? (
        <EmptyState message={t("eventEditor.questions.empty")} />
      ) : null}

      {items.length > 0 ? (
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {items.map((question) => (
            <QuestionRow
              key={question.id}
              question={question}
              busy={update.isPending}
              onUpdate={(patch) => update.mutate({ id: question.id, patch })}
              formatDate={formatDate}
            />
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}

function QuestionRow({
  question,
  busy,
  onUpdate,
  formatDate,
}: {
  question: EventQuestionAdmin;
  busy: boolean;
  onUpdate: (patch: EventQuestionAdminUpdate) => void;
  formatDate: (value: string, options?: Intl.DateTimeFormatOptions) => string;
}) {
  const { t } = useTranslation();
  const answered = question.answered_at !== null;

  return (
    <Box
      component="li"
      data-testid="event.question"
      data-answered={answered ? "true" : "false"}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: `${RADIUS.md}px`,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        opacity: question.is_hidden ? 0.6 : 1,
      }}
    >
      <Typography
        variant="T1"
        sx={{
          color: "text.primary",
          textDecoration: answered ? "line-through" : "none",
        }}
      >
        {question.body}
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography variant="L2" sx={{ color: "text.secondary" }}>
          {question.author_name}
        </Typography>
        <Typography variant="T3" sx={{ color: "text.muted" }}>
          · {t(`eventEditor.questions.source.${question.source}`)} ·{" "}
          <time dateTime={question.created_at}>
            {formatDate(question.created_at, {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </time>
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={answered ? "success" : "default"}
          label={t(
            answered
              ? "eventEditor.questions.answered"
              : "eventEditor.questions.pending",
          )}
        />
        {question.is_hidden ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("eventEditor.questions.hidden")}
          />
        ) : null}
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant={answered ? "text" : "outlined"}
          disabled={busy}
          onClick={() => onUpdate({ answered: !answered })}
          data-testid="event.question-toggle-answered"
        >
          {t(
            answered
              ? "eventEditor.questions.markUnanswered"
              : "eventEditor.questions.markAnswered",
          )}
        </Button>
        <Button
          size="small"
          variant="text"
          color="inherit"
          disabled={busy}
          onClick={() => onUpdate({ is_hidden: !question.is_hidden })}
          data-testid="event.question-toggle-hidden"
        >
          {t(
            question.is_hidden
              ? "eventEditor.questions.unhide"
              : "eventEditor.questions.hide",
          )}
        </Button>
      </Box>
    </Box>
  );
}
